// Cache tags for the generated user endpoints, kept separate from the api wiring in
// `userEnhanced.ts` so the policy can be read and tested without pulling in the whole store.
// Deliberately import-free.

const USERS_LIST = { type: 'Users' as const, id: 'LIST' }
// The Archive tab is its own query; archiving and unarchiving move rows between the two lists,
// so those writes invalidate both. archiveUser belongs in that set: the api soft-deletes the row
// into the archive.
const ARCHIVED_USERS_LIST = { type: 'Users' as const, id: 'ARCHIVED_LIST' }
const IDENTITY_LINKS = { type: 'IdentityLinks' as const, id: 'LIST' }

const userDismissals = (_result: unknown, _error: unknown, arg: { tenantId: string; userId: string }) => [
  { type: 'UserDismissals' as const, id: `${arg.tenantId}:${arg.userId}` },
]

// The learners endpoint answers paginated ({ items }) or as a plain array depending on its
// params; both shapes get per-row tags so row-level invalidation can land later without a
// policy change. The guards also keep error payloads out of the tag mapper.
const providesUsersList = (result: { items?: { id: string }[] } | { id: string }[] | undefined) => {
  if (!result) return [USERS_LIST]

  if ('items' in result && Array.isArray(result.items)) {
    return [...result.items.map(({ id }) => ({ type: 'Users' as const, id })), USERS_LIST]
  }

  if (Array.isArray(result)) {
    return [...result.map(({ id }) => ({ type: 'Users' as const, id })), USERS_LIST]
  }

  return [USERS_LIST]
}

// updateUserSettings is deliberately absent: it saves the signed-in user's own UI preference,
// and the language flow resets the whole cache instead.
export const userCacheTags = {
  listUserDismissals: {
    providesTags: userDismissals,
  },
  dismissUserNotice: {
    invalidatesTags: userDismissals,
  },
  listTenantUsers: {
    providesTags: providesUsersList,
  },
  listArchivedUsers: {
    providesTags: [ARCHIVED_USERS_LIST],
  },
  createUser: {
    invalidatesTags: [USERS_LIST],
  },
  archiveUser: {
    invalidatesTags: [USERS_LIST, ARCHIVED_USERS_LIST],
  },
  importUsers: {
    invalidatesTags: [USERS_LIST],
  },
  archiveUsers: {
    invalidatesTags: [USERS_LIST, ARCHIVED_USERS_LIST],
  },
  unarchiveUsers: {
    invalidatesTags: [USERS_LIST, ARCHIVED_USERS_LIST],
  },
  deleteArchivedUsers: {
    invalidatesTags: [ARCHIVED_USERS_LIST],
  },
  listIdentityLinks: {
    providesTags: [IDENTITY_LINKS],
  },
  unlinkIdentityProviders: {
    invalidatesTags: [IDENTITY_LINKS],
  },
  inviteUsers: {
    invalidatesTags: [USERS_LIST],
  },
  updateUser: {
    invalidatesTags: [USERS_LIST],
  },
  updateUserStatus: {
    invalidatesTags: [USERS_LIST],
  },
  createOrUpgradeGlobalAdmin: {
    invalidatesTags: [USERS_LIST],
  },
  assignUsersToUnits: {
    invalidatesTags: [USERS_LIST],
  },
  unassignUsersFromUnits: {
    invalidatesTags: [USERS_LIST],
  },
  bulkUnassignUsersFromUnits: {
    invalidatesTags: [USERS_LIST],
  },
} as const
